import React, { useState, useMemo, useEffect } from 'react';
import { Box, Button, Input, Select, Table, Thead, Tbody, Tr, Th, Td, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, Textarea, useDisclosure, Flex, Heading, Text, FormControl, FormLabel, IconButton, useColorModeValue, Spinner, HStack, useToast, Badge } from '@chakra-ui/react';
import { FaTrash, FaEdit, FaPlus, FaAngleLeft, FaAngleRight } from 'react-icons/fa';
import client from '../utils/fetchClient';

function formatDate(date) {
  if (!date) return '-';
  const d = new Date(date);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// Deprecated in favor of stateful pageSize

function CreateProjectModal({ open, onClose, onCreate }) {
  const [name, setName] = useState('');
  const [env, setEnv] = useState('Test');
  const [description, setDescription] = useState('');
  const [defaultBranch, setDefaultBranch] = useState('main');
  const [tags, setTags] = useState('');
  const [error, setError] = useState('');

  const modalBg = useColorModeValue('white', 'gray.700');
  const inputBg = useColorModeValue('gray.50', 'gray.600');
  const inputBorder = useColorModeValue('gray.200', 'gray.600');

  function handleCreate() {
    if (!name.trim()) {
      setError('Project name is required');
      return;
    }
    onCreate({ name: name.trim(), env: env.trim(), description: description.trim(), defaultBranch: defaultBranch.trim(), tags: tags.trim(), createdAt: new Date().toISOString() });
    setName(''); setEnv('Test'); setDescription(''); setDefaultBranch('main'); setTags(''); setError('');
  }
  return (
    <Modal isOpen={open} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent bg={modalBg}>
        <ModalHeader>Create Project</ModalHeader>
        <ModalBody display="flex" flexDirection="column" gap={3}>
          <FormControl>
            <FormLabel>Project Name</FormLabel>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Enter project name" bg={inputBg} borderColor={inputBorder} />
          </FormControl>
          <FormControl>
             <FormLabel>Environment</FormLabel>
            <Select value={env} onChange={e => setEnv(e.target.value)} bg={inputBg} borderColor={inputBorder}>
              <option value="Test">Test</option>
              <option value="Staging">Staging</option>
              <option value="Production">Production</option>
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel>Default Branch</FormLabel>
            <Input value={defaultBranch} onChange={e => setDefaultBranch(e.target.value)} placeholder="e.g., main or master" bg={inputBg} borderColor={inputBorder} />
          </FormControl>
          <FormControl>
             <FormLabel>Description</FormLabel>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Enter project description" bg={inputBg} borderColor={inputBorder} />
          </FormControl>
          <FormControl>
            <FormLabel>Tags</FormLabel>
            <Input value={tags} onChange={e => setTags(e.target.value)} placeholder="Comma-separated (e.g., ui,regression)" bg={inputBg} borderColor={inputBorder} />
          </FormControl>
          {error && <Box color="red.500" fontSize="sm">{error}</Box>}
        </ModalBody>
        <ModalFooter gap={2}>
          <Button onClick={onClose} variant="ghost">Cancel</Button>
          <Button colorScheme="green" onClick={handleCreate}>Create</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default function SettingsProjectsTab({ readOnly = false }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editIdx, setEditIdx] = useState(null);
  const [editData, setEditData] = useState({ name: '', env: '', description: '' });
  const { isOpen: isConfirmOpen, onOpen: onOpenConfirm, onClose: onCloseConfirm } = useDisclosure();
  const [deleteIdx, setDeleteIdx] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const { isOpen: isCreateOpen, onOpen: onOpenCreate, onClose: onCloseCreate } = useDisclosure();
  const [pageSize, setPageSize] = useState(5);
  const toast = useToast();
  const { isOpen: linkOpen, onOpen: onOpenLink, onClose: onCloseLink } = useDisclosure();
  const [linkTargetIdx, setLinkTargetIdx] = useState(null);
  const [availableKeys, setAvailableKeys] = useState([]);
  const [selectedKeyId, setSelectedKeyId] = useState('');

  // Chakra color mode values
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const tableHeaderBg = useColorModeValue('green.50', 'gray.700');
  const tableHeaderColor = useColorModeValue('green.700', 'green.200');
  const tableRowBgEven = useColorModeValue('white', 'gray.800');
  const tableRowBgOdd = useColorModeValue('gray.50', 'gray.700');
  const inputBg = useColorModeValue('gray.50', 'gray.700');
  const inputBorder = useColorModeValue('gray.200', 'gray.600');

  // Load projects from backend or localStorage
  useEffect(() => {
    async function loadProjects() {
      setLoading(true);
      setError('');
      try {
        // Try backend first
        const res = await client.get('/api/projects');
        if (res.ok) {
          const data = await res.json();
          setProjects(Array.isArray(data) ? data : (data.projects || []));
        } else {
          // fallback to localStorage
          const local = localStorage.getItem('projects');
          setProjects(local ? JSON.parse(local) : []);
        }
      } catch (e) {
        const local = localStorage.getItem('projects');
        setProjects(local ? JSON.parse(local) : []);
        setError('Failed to load projects from backend. Showing local data.');
      } finally {
        setLoading(false);
      }
    }
    loadProjects();
  }, []);

  const loadKeys = async () => {
    try {
      const res = await client.get('/api/keys');
      if (res.ok) {
        const result = await res.json();
        setAvailableKeys(result.keys || []);
      } else {
        setAvailableKeys([]);
      }
    } catch (e) {
      setAvailableKeys([]);
    }
  };

  // Filtered and paginated projects
  const filtered = useMemo(() => {
    const q = (search || '').toLowerCase();
    if (!q) return projects || [];
    return (projects || []).filter(p =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.env || '').toLowerCase().includes(q) ||
      (p.description || '').toLowerCase().includes(q) ||
      (p.defaultBranch || '').toLowerCase().includes(q) ||
      (String(p.tags || '')).toLowerCase().includes(q)
    );
  }, [projects, search]);
  const totalPages = useMemo(() => Math.max(1, Math.ceil((filtered?.length || 0) / pageSize)), [filtered, pageSize]);
  const paged = useMemo(() => (filtered || []).slice((page - 1) * pageSize, page * pageSize), [filtered, page, pageSize]);

  function startEdit(idx) {
    const globalIdx = (page - 1) * pageSize + idx;
    setEditIdx(globalIdx);
    setEditData(projects[globalIdx]);
  }
  function saveEdit() {
    const updated = [...projects];
    updated[editIdx] = { ...editData };
    setProjects(updated);
    localStorage.setItem('projects', JSON.stringify(updated));
    setEditIdx(null);
    toast({ title: 'Project updated.', status: 'success', duration: 2000, isClosable: true });
  }
  function cancelEdit() {
    setEditIdx(null);
  }
  function confirmDelete(idx) {
    const globalIdx = (page - 1) * pageSize + idx;
    setDeleteIdx(globalIdx);
    onOpenConfirm();
  }
  function doDelete() {
    const updated = projects.filter((_, i) => i !== deleteIdx);
    setProjects(updated);
    localStorage.setItem('projects', JSON.stringify(updated));
    onCloseConfirm();
    setDeleteIdx(null);
  }
  function handleSearch(e) {
    setSearch(e.target.value);
    setPage(1);
  }
  function handlePageChange(newPage) {
    setPage(newPage);
  }
  function handleCreateProject(proj) {
    const updated = [...projects, proj];
    setProjects(updated);
    localStorage.setItem('projects', JSON.stringify(updated));
    onCloseCreate();
  }

  if (loading) {
    return <Flex align="center" justify="center" minH="120px"><Spinner /></Flex>;
  }
  if (error) {
    return <Text color="red.500" mb={2}>{error}</Text>;
  }

  return (
    <Box>
      <Heading as="h3" size="md" mb={2} color={textColor}>Projects</Heading>
      <Text fontSize="sm" color="gray.500" mb={6}>Manage your Playwright projects and environments.</Text>
      {readOnly && (
        <Text fontSize="xs" color="gray.400" mb={3}>You have read-only access to projects.</Text>
      )}
      <Flex align="center" mb={6} gap={4} flexWrap="wrap">
        {!readOnly && (
          <Button leftIcon={<FaPlus />} colorScheme="green" onClick={() => onOpenCreate()} size="sm" borderRadius="lg">New Project</Button>
        )}
        <HStack ml="auto" gap={2}>
          <Input
            placeholder="Search projects..."
            value={search}
            onChange={handleSearch}
            size="sm"
            borderRadius="full"
            maxW="220px"
            minW="120px"
            bg={inputBg}
            borderColor={inputBorder}
          />
          <Select size="sm" value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
          </Select>
        </HStack>
      </Flex>
      <CreateProjectModal open={isCreateOpen} onClose={onCloseCreate} onCreate={handleCreateProject} />

      <Box overflowX="auto" borderRadius="lg" boxShadow="md" borderWidth={1} borderColor={borderColor} bg={cardBg}>
        <Table variant="simple" size="sm">
          <Thead bg={tableHeaderBg}>
            <Tr>
              <Th color={tableHeaderColor} py={3}>Name</Th>
              <Th color={tableHeaderColor} py={3}>Environment</Th>
              <Th color={tableHeaderColor} py={3}>Default Branch</Th>
              <Th color={tableHeaderColor} py={3}>Tags</Th>
              <Th color={tableHeaderColor} py={3}>Created</Th>
              <Th py={3}></Th>
            </Tr>
          </Thead>
          <Tbody>
            {(paged || []).map((p, i) => {
              const globalIdx = (page - 1) * pageSize + i;
              const isEditing = editIdx === globalIdx;
              return (
                <Tr key={p.name || i} bg={i % 2 === 0 ? tableRowBgEven : tableRowBgOdd}>
                  <Td color={textColor}>
                    {isEditing ? (
                      <Input value={editData.name} onChange={e => setEditData(d => ({ ...d, name: e.target.value }))} size="sm" bg={inputBg} borderColor={inputBorder} />
                    ) : p.name}
                  </Td>
                  <Td color={textColor}>
                    {isEditing ? (
                      <Select value={editData.env} onChange={e => setEditData(d => ({ ...d, env: e.target.value }))} size="sm" bg={inputBg} borderColor={inputBorder}>
                        <option value="Test">Test</option>
                        <option value="Staging">Staging</option>
                        <option value="Production">Production</option>
                      </Select>
                    ) : p.env}
                  </Td>
                  <Td color={textColor}>
                    {isEditing ? (
                      <Input value={editData.defaultBranch || ''} onChange={e => setEditData(d => ({ ...d, defaultBranch: e.target.value }))} size="sm" bg={inputBg} borderColor={inputBorder} />
                    ) : (p.defaultBranch || '-')}
                  </Td>
                  <Td color={textColor}>
                    {isEditing ? (
                      <Input value={editData.tags || ''} onChange={e => setEditData(d => ({ ...d, tags: e.target.value }))} size="sm" width={180} bg={inputBg} borderColor={inputBorder} />
                    ) : (
                      (p.tags ? String(p.tags) : '').split(',').filter(Boolean).map(tag => (
                        <Badge key={tag} colorScheme="green" mr={1}>{tag.trim()}</Badge>
                      ))
                    )}
                  </Td>
                  <Td color={textColor}>{formatDate(p.createdAt)}</Td>
                  <Td textAlign="right">
                    {readOnly ? (
                      <Text fontSize="xs" color="gray.400">View only</Text>
                    ) : isEditing ? (
                      <>
                        <Button size="xs" colorScheme="green" mr={2} onClick={saveEdit}>Save</Button>
                        <Button size="xs" variant="ghost" onClick={cancelEdit}>Cancel</Button>
                      </>
                    ) : (
                      <HStack justify="flex-end">
                        <Button size="xs" variant="outline" onClick={async () => { setLinkTargetIdx(globalIdx); await loadKeys(); onOpenLink(); }}>Link API Key</Button>
                        <IconButton icon={<FaEdit />} aria-label="Edit" size="xs" colorScheme="green" onClick={() => startEdit(i)} />
                        <IconButton icon={<FaTrash />} aria-label="Delete" size="xs" colorScheme="red" onClick={() => confirmDelete(i)} />
                      </HStack>
                    )}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
      <Flex align="center" justify="center" mt={4} gap={2}>
        <IconButton icon={<FaAngleLeft />} aria-label="Prev" size="sm" onClick={() => handlePageChange(Math.max(1, page - 1))} isDisabled={page === 1} />
        <Text fontSize="sm">Page {page} of {totalPages}</Text>
        <IconButton icon={<FaAngleRight />} aria-label="Next" size="sm" onClick={() => handlePageChange(Math.min(totalPages, page + 1))} isDisabled={page === totalPages} />
      </Flex>
      <Modal isOpen={isConfirmOpen} onClose={onCloseConfirm} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Project</ModalHeader>
          <ModalBody>Are you sure you want to delete this project?</ModalBody>
          <ModalFooter>
            <Button onClick={onCloseConfirm} mr={3}>Cancel</Button>
            <Button colorScheme="red" onClick={doDelete}>Delete</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal isOpen={linkOpen} onClose={() => { setSelectedKeyId(''); onCloseLink(); }} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Link API Key to Project</ModalHeader>
          <ModalBody>
            <FormControl>
              <FormLabel>Select API Key</FormLabel>
              <Select placeholder="Select key" value={selectedKeyId} onChange={e => setSelectedKeyId(e.target.value)}>
                {availableKeys.map(k => (
                  <option key={k.id} value={k.id}>{k.keyPrefix} — {k.status}</option>
                ))}
              </Select>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={() => { setSelectedKeyId(''); onCloseLink(); }}>Cancel</Button>
            <Button colorScheme="green" isDisabled={!selectedKeyId} onClick={() => {
              if (linkTargetIdx != null) {
                const updated = [...projects];
                const proj = { ...updated[linkTargetIdx], apiKeyId: selectedKeyId };
                updated[linkTargetIdx] = proj;
                setProjects(updated);
                localStorage.setItem('projects', JSON.stringify(updated));
                toast({ title: 'Project linked to API key.', status: 'success', duration: 2000, isClosable: true });
              }
              setSelectedKeyId('');
              onCloseLink();
            }}>Link</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}